import PageShell from '@/pages/shared/PageShell';
import Button from '@/components/base/Button';

export default function TemplateWBS() {
  return (
    <PageShell
      title="WBSテンプレート無料ダウンロード｜作業分解構成図 Excel｜”おもいむすび”"
      description="WBS（Work Breakdown Structure）作成用のExcelテンプレートを無料ダウンロード。作業項目の階層構造、成果物、担当者、工数を見やすく整理できるWBSテンプレートです。"
      keywords="WBS Excel テンプレート,WBS テンプレート,作業分解構成図 テンプレート,プロジェクト管理 WBS Excel,プロジェクト管理 作業項目"
      canonical="https://omoimusubi.asia/templates/wbs/"
      h1="WBSテンプレート（無料ダウンロード）"
      subtitle="WBS（Work Breakdown Structure：作業分解構成図）作成用のExcelテンプレートです。作業項目の階層構造、成果物、担当者、工数を整理できます。"
      relatedLinks={[
        { label: 'プロジェクト管理Excelテンプレート', path: '/templates/project-management-excel/' },
        { label: 'ガントチャートExcelテンプレート', path: '/templates/gantt-chart-excel/' },
        { label: 'WBSとは？作り方とガントチャートとの違い', path: '/guides/wbs/' },
        { label: 'AIタスク分解', path: '/features/ai-task-breakdown/' },
      ]}
    >
      <div className="not-prose my-8 p-8 bg-primary-50 rounded-lg border border-primary-200/70 text-center">
        <div className="w-16 h-16 mx-auto bg-secondary-100 rounded-full flex items-center justify-center mb-4">
          <i className="ri-node-tree text-secondary-600 text-2xl"></i>
        </div>
        <h2 className="text-xl font-bold text-foreground-900 mb-2">WBSテンプレート</h2>
        <p className="text-sm text-foreground-600 mb-6">作業項目、成果物、担当者、工数を整理できるWBS作成用テンプレートです。</p>
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {['大項目','中項目','小項目','成果物','担当者','工数','開始日','終了日','備考'].map((tag) => (
            <span key={tag} className="px-3 py-1 bg-background-50 text-xs text-foreground-600 rounded-full border border-background-200">{tag}</span>
          ))}
        </div>
        <a href="/templates/wbs-template.xlsx" download className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-3 text-base transition-colors cursor-pointer"><i className="ri-download-2-line"></i>無料ダウンロード（Excel）</a><p className="text-xs text-foreground-500 mt-3">会員登録不要・無料でお使いいただけます。</p>
      </div>

      <section>
        <h2>WBSテンプレートの項目構成</h2>
        <p>このテンプレートは、WBSの基本である階層構造をExcel上で再現できるように設計されています。</p>
        <ul>
          <li><strong>レベル（L1/L2/L3）</strong>：作業の階層レベル。L1が最上位（プロジェクト全体）、L2が中分類、L3が具体的な作業単位です。</li>
          <li><strong>作業項目名</strong>：各レベルの作業名称。L3では「完了/未完了」が明確に判断できる表現にします。</li>
          <li><strong>成果物</strong>：その作業が完了したときに生み出される具体的な成果物。例：「設計書」「テスト結果報告書」</li>
          <li><strong>担当者</strong>：作業の責任者。L3レベルで1名を割り当てます。</li>
          <li><strong>工数（人日）</strong>：作業にかかる想定工数。</li>
          <li><strong>開始日・終了日</strong>：作業の予定期間。ガントチャート作成の基礎データになります。</li>
        </ul>
      </section>

      <section>
        <h2>WBS作成の手順</h2>
        <ol>
          <li><strong>最終成果物を決める</strong>：プロジェクトのゴールとなる成果物を最上位（L1）に設定します。</li>
          <li><strong>成果物を分解する</strong>：L1の成果物を作るために必要な中間成果物をL2として列挙します。</li>
          <li><strong>作業レベルまで分解する</strong>：L2の各項目を、具体的な作業単位（L3）にまで分解します。目安は1作業あたり1〜5人日です。</li>
          <li><strong>漏れがないか確認する</strong>：すべての成果物がL2/L3でカバーされているか、関係者とレビューします。</li>
          <li><strong>ガントチャートに展開する</strong>：完成したWBSをもとに、各作業を時系列に並べてガントチャートを作成します。</li>
        </ol>
      </section>

      <section>
        <h2>WBS作成のコツ</h2>
        <ul>
          <li><strong>成果物ベースで考える</strong>：「何をするか」ではなく「何を作るか」で分解すると、作業の抜け漏れが減ります。</li>
          <li><strong>100%ルール</strong>：下位レベルの作業をすべて合計すると、上位レベルの作業を100%カバーしている状態が理想です。</li>
          <li><strong>適切な粒度を保つ</strong>：細かすぎると管理が煩雑に、粗すぎると進捗把握が難しくなります。1タスク1〜5人日を目安にしてください。</li>
          <li><strong>管理できないものは分解しない</strong>：外部委託部分など、詳細を管理できない作業は無理に分解しないようにしましょう。</li>
        </ul>
      </section>

      <section>
        <h2>AIによるWBS自動作成</h2>
        <p>WBSの作成はプロジェクト管理の重要なステップですが、手作業では時間がかかります。”おもいむすび”のAIタスク分解機能を使えば、企画書や提案書をアップロードするだけで、AIが自動でWBSを生成します。作業項目、担当者候補、期限、依存関係まで提案されるため、WBS作成にかかる時間を大幅に削減できます。無料お試し（期限なし）で、ぜひ体験してください。</p>
      </section>
    </PageShell>
  );
}